import clsx from 'clsx';
import styles from './studyUsers.module.scss';
import TablePageLayout from 'src/components/tablePageLayout/TablePageLayout';
import { FC, useCallback, useMemo, useState } from 'react';
import { DecodedValueMap, SetQuery } from 'use-query-params';
import { DirectionalHint } from '@fluentui/react';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { useStudy } from 'src/helpers/hooks/useStudy';
import { useGetStudyRolesQuery } from 'src/services/api/api-client/UserRolesQuery';
import { AppButton } from 'uikit/buttons';
import { AppButtonWithTooltip } from 'uikit/buttons/buttonWithTooltip/buttonWithTooltip';
import { DropDownMenuOption } from 'uikit/dropDownMenu/dropDownMenu';
import { DropDownMenu } from 'uikit/dropDownMenu/dropDownMenu.component';
import { ReactComponent as CheckboxUncheckedIcon } from 'src/assets/img/issues/checkbox_unchecked_16.svg';
import { ReactComponent as CheckboxCheckedIcon } from 'src/assets/img/issues/checkbox_checked_16.svg';
import { ReactComponent as ResetIcon } from 'src/assets/img/issues/reset_16.svg';
import { ReactComponent as FilterIcon } from 'src/assets/img/common/filter_20.svg';
import { ReactComponent as Reset24Icon } from 'src/assets/img/common/reset_24.svg';
import { defaultQueryParams, usersQueryParamsConfig } from './studyUserQueryParamsHelper';
import { useGetStudyStaffTagsQuery } from 'src/services/api/api-client/StudyQuery';
import { debounce } from 'lodash';

export const Filters: FC<{
  queryParams: DecodedValueMap<typeof usersQueryParamsConfig>;
  setQueryParams: SetQuery<typeof usersQueryParamsConfig>;
}> = ({ queryParams, setQueryParams }) => {
  const { showButtonComponent, filters } = useFilters(queryParams, setQueryParams);

  return (
    <>
      <TablePageLayout.Header.Row>
        {showButtonComponent}
        <SearchInput queryParams={queryParams} setQueryParams={setQueryParams} />
      </TablePageLayout.Header.Row>
      {filters}
    </>
  );
};

const useFilters = (
  queryParams: DecodedValueMap<typeof usersQueryParamsConfig>,
  setQueryParams: SetQuery<typeof usersQueryParamsConfig>,
) => {
  const applyFilterCount = useMemo(
    () => Object.keys(queryParams.tags).length + Number(!!queryParams.roles?.length),
    [queryParams],
  );

  const [isOpen, setIsOpen] = useState<boolean>(!!applyFilterCount);
  const { t } = useScopedTranslation('StudyUsersPage.Filters');

  return {
    showButtonComponent: (
      <div className={styles.filterButtonContainer}>
        <AppButton
          variant={'icon-link'}
          colorSchema={'secondary'}
          Icon={FilterIcon}
          onClick={() => setIsOpen((s) => !s)}
          text={isOpen ? t('HideFilters') : t('ShowFilters')}
        />
        {applyFilterCount > 0 && (
          <div className={styles.appliedFilters}>
            {!isOpen && <div>{t('AppliedCount', { count: applyFilterCount })}</div>}
            <AppButtonWithTooltip
              tooltip={t('Reset')}
              variant={'icon-link'}
              colorSchema={'destroy'}
              Icon={Reset24Icon}
              onClick={() => setQueryParams(defaultQueryParams)}
              disabled={!applyFilterCount}
            />
          </div>
        )}
      </div>
    ),
    filters: isOpen ? (
      <TablePageLayout.Header.Row className={styles.filtersRow}>
        <RolesFilter queryParams={queryParams} setQueryParams={setQueryParams} />
        <TagsFilter queryParams={queryParams} setQueryParams={setQueryParams} />
      </TablePageLayout.Header.Row>
    ) : null,
  };
};

const TagsFilter: FC<{
  queryParams: DecodedValueMap<typeof usersQueryParamsConfig>;
  setQueryParams: SetQuery<typeof usersQueryParamsConfig>;
}> = ({ queryParams, setQueryParams }) => {
  const { t } = useScopedTranslation('StudyUsersPage.Filters');
  const { id: studyId } = useStudy() ?? {};
  const { data: studyTags } = useGetStudyStaffTagsQuery(studyId!);

  const toggleTags = useCallback(
    (key: string, values: string[] | undefined) => {
      const tags = { ...queryParams.tags };
      // This is work around for bug in react-query
      tags[key] = [...(tags[key] ?? [])];
      for (const value of values ?? []) {
        const index: number = tags[key].indexOf(value);
        if (index === -1) tags[key].push(value);
        else tags[key].splice(index, 1);
      }

      if (!tags[key].length) delete tags[key];

      const tagsCount = Object.keys(tags).length;
      setQueryParams({ tags: tagsCount ? tags : undefined });
    },
    [queryParams, setQueryParams],
  );

  const multivaluedTagFilterVms = useMemo(
    () =>
      Object.entries(studyTags ?? {})
        .map(([key, values]) => {
          const firstCheckedValue = queryParams.tags[key]?.[0];
          const checkedValueCount = queryParams.tags[key]?.length ?? 0;

          return {
            anySelected: checkedValueCount > 0,
            key: key,
            text: firstCheckedValue ? `${key}: ${firstCheckedValue}` : key,
            counter: checkedValueCount > 1 ? `(+${checkedValueCount - 1})` : '',
            options: values.map((value: string) => {
              const checked = !!queryParams.tags[key]?.includes(value);
              return {
                key: value,
                icon: checked ? <CheckboxCheckedIcon /> : <CheckboxUncheckedIcon />,
                text: value,
                checked,
                action: () => toggleTags(key, [value]),
                className: clsx(styles.commonOption, styles.fillNone),
              } as DropDownMenuOption;
            }),
          };
        })
        .flat(),
    [studyTags, queryParams.tags, toggleTags],
  );

  const renderResetButton = useCallback(
    (tagKey: string) => (
      <div
        data-test-id={'menu-item'}
        className={styles.resetButton}
        onClick={(e) => {
          toggleTags(tagKey, queryParams.tags[tagKey]);
          e?.stopPropagation();
        }}
      >
        <ResetIcon />
        <div>{t('Reset')}</div>
      </div>
    ),
    [queryParams.tags, t, toggleTags],
  );

  return (
    <div className={styles.tagsFilterContainer}>
      {multivaluedTagFilterVms.map((vm) => (
        <DropDownMenu
          key={vm.key}
          colorSchema={vm.anySelected ? 'primary' : 'black'}
          text={vm.text}
          counter={vm.counter}
          options={vm.options}
          closeCalloutByClick={false}
          directionalHint={DirectionalHint.bottomLeftEdge}
          hasChevron={true}
          calloutFooter={renderResetButton(vm.key)}
        />
      ))}
    </div>
  );
};

const RolesFilter: FC<{
  queryParams: DecodedValueMap<typeof usersQueryParamsConfig>;
  setQueryParams: SetQuery<typeof usersQueryParamsConfig>;
}> = ({ queryParams, setQueryParams }) => {
  const { t } = useScopedTranslation('StudyUsersPage.RolesFilter');
  const rolesQuery = useGetStudyRolesQuery();

  const roleOptions = useMemo(
    () =>
      rolesQuery.data?.map(
        (role) =>
          ({
            key: role.name,
            icon: queryParams.roles?.includes(role.name) ? <CheckboxCheckedIcon /> : <CheckboxUncheckedIcon />,
            text: role.name,
            checked: queryParams.roles?.includes(role.name),
            action: () =>
              setQueryParams({
                roles: queryParams.roles?.includes(role.name)
                  ? queryParams.roles.filter((x) => x !== role.name)
                  : [...(queryParams.roles || []), role.name],
                page: 1,
              }),
            className: clsx(styles.commonOption, styles.fillNone),
          } as DropDownMenuOption),
      ) ?? [],
    [queryParams.roles, rolesQuery.data, setQueryParams],
  );

  const selectedRoleFilters = useMemo(() => {
    const roleFilter = queryParams?.roles ?? [];
    const anySelected = roleFilter.length > 0;
    return {
      anySelected,
      filtersCount: roleFilter.length,
      textCounter: roleFilter.length > 1 ? `(+${roleFilter.length - 1})` : '',
      filterText: anySelected ? roleFilter[0] || '' : t('Title'),
    };
  }, [queryParams?.roles, t]);

  const renderResetButton = useCallback(
    (action: () => void) => (
      <div
        data-test-id={'menu-item'}
        className={styles.resetButton}
        onClick={(e) => {
          e?.stopPropagation();
          action();
        }}
      >
        <ResetIcon />
        <div>{t('Reset')}</div>
      </div>
    ),
    [t],
  );

  const resetRoleFilters = useCallback(() => {
    setQueryParams({
      roles: undefined,
      page: 1,
    });
  }, [setQueryParams]);

  return (
    <DropDownMenu
      colorSchema={selectedRoleFilters.anySelected ? 'primary' : 'black'}
      text={selectedRoleFilters.filterText}
      counter={selectedRoleFilters.textCounter}
      options={roleOptions}
      closeCalloutByClick={false}
      directionalHint={DirectionalHint.bottomLeftEdge}
      hasChevron={true}
      testId={'role-filter-menu'}
      calloutFooter={renderResetButton(resetRoleFilters)}
    />
  );
};

const SearchInput: FC<{
  queryParams: DecodedValueMap<typeof usersQueryParamsConfig>;
  setQueryParams: SetQuery<typeof usersQueryParamsConfig>;
}> = ({ queryParams, setQueryParams }) => {
  const { t } = useScopedTranslation('StudyUsersPage');

  const debounceFunc = debounce(
    (value: string | undefined) => setQueryParams((prev) => ({ ...prev, searchQuery: value })),
    200,
  );

  return (
    <TablePageLayout.Header.Search
      defaultValue={queryParams.searchQuery || ''}
      placeholder={t('Search')}
      onChange={(e) => {
        e.preventDefault();
        debounceFunc(e.target.value || undefined);
      }}
    />
  );
};
